'use client';

import React, { useState, useMemo, useEffect, useCallback } from 'react';
import AppLayout from '@/components/AppLayout';
import {
  ClipboardCheck,
  Clock,
  Play,
  CheckCircle2,
  MessageSquarePlus,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  User,
  Timer,
  Tag,
  MapPin,
  Server,
  Link2,
  Send,
  X,
  Filter,
  Search,
  Pause,
  RotateCcw,
  Activity,
  CalendarClock,
  Zap,
} from 'lucide-react';
import Icon from '@/components/ui/AppIcon';
import {
  sendWorkOrderStatusChangeNotification,
  sendSlaApproachingNotification,
  onEmailDelivery,
  type EmailDeliveryResult,
} from '@/lib/notifications/workOrderNotifications';
import { ToastContainer, useToast } from '@/components/ui/Toast';
import WorkOrderComments from './components/WorkOrderComments';


// ─── Types ────────────────────────────────────────────────────────────────────

type Priority = 'P1' | 'P2' | 'P3' | 'P4';
type ExecStatus = 'assigned' | 'claimed' | 'in_progress' | 'paused' | 'completed';

interface UpdateLog {
  id: string;
  timestamp: string;
  author: string;
  authorAvatar: string;
  message: string;
  type: 'claim' | 'start' | 'pause' | 'resume' | 'update' | 'complete';
}

interface WorkOrderExec {
  id: string;
  incidentId: string;
  incidentTitle: string;
  priority: Priority;
  slaDeadline: string;
  slaRemaining: string;
  slaBreaching: boolean;
  type: string;
  region: string;
  node: string;
  status: ExecStatus;
  assigneeId: string | null;
  assigneeName: string | null;
  assigneeAvatar: string | null;
  createdAt: string;
  estimatedDuration: string;
  elapsedTime: string;
  customer: string;
  description: string;
  logs: UpdateLog[];
}

// ─── Mock Data ────────────────────────────────────────────────────────────────

const currentUser = { id: 'tm-1', name: 'Dawit Taddese', avatar: 'DT', role: 'Network Engineer' };

const initialWorkOrders: WorkOrderExec[] = [
  {
    id: 'WO-1041',
    incidentId: 'INC-4821',
    incidentTitle: 'Remote node receiver offline — 18 residential units affected',
    priority: 'P1',
    slaDeadline: '09:44',
    slaRemaining: '8m',
    slaBreaching: true,
    type: 'Remote Node Down',
    region: 'Bole',
    node: 'RN-BOLE-APT-07',
    status: 'claimed',
    assigneeId: 'tm-1',
    assigneeName: 'Dawit Taddese',
    assigneeAvatar: 'DT',
    createdAt: '09:14',
    estimatedDuration: '45m',
    elapsedTime: '12m',
    customer: 'Maple Court Apartments (MDU — 18 residents)',
    description: 'Tarana remote node receiver RN-BOLE-APT-07 is offline, cutting service to 18 residential units sharing this node. Check power, physical alignment, and RF link status to the base node BN-BOLE-NORTH-01. Verify Tarana G1 firmware and re-establish beam-forming session.',
    logs: [
      { id: 'log-1', timestamp: '09:14', author: 'System', authorAvatar: 'SY', message: 'Work order created from INC-4821', type: 'update' },
      { id: 'log-2', timestamp: '09:16', author: 'Dawit Taddese', authorAvatar: 'DT', message: 'Claimed work order. Checking Tarana NMS for RN-BOLE-APT-07 status and last known RF metrics.', type: 'claim' },
    ],
  },
  {
    id: 'WO-1039',
    incidentId: 'INC-4819',
    incidentTitle: 'Remote node receiver unreachable — ICMP timeout all probes',
    priority: 'P1',
    slaDeadline: '09:29',
    slaRemaining: '15m',
    slaBreaching: false,
    type: 'Remote Node Down',
    region: 'Yeka',
    node: 'RN-YEKA-BIZ-02',
    status: 'in_progress',
    assigneeId: 'tm-2',
    assigneeName: 'Selam Bekele',
    assigneeAvatar: 'SB',
    createdAt: '09:14',
    estimatedDuration: '60m',
    elapsedTime: '18m',
    customer: 'Bayview Business Park (Business Complex — 9 tenants)',
    description: 'Tarana remote node RN-YEKA-BIZ-02 serving Bayview Business Park is not responding to ICMP probes from 3 monitoring stations. Last successful ping at 09:11. 9 business tenants are without service. Physical layer check required — possible power or mounting hardware failure.',
    logs: [
      { id: 'log-1', timestamp: '09:14', author: 'System', authorAvatar: 'SY', message: 'Work order created from INC-4819', type: 'update' },
      { id: 'log-2', timestamp: '09:15', author: 'Selam Bekele', authorAvatar: 'SB', message: 'Claimed. Contacting building facilities manager at Bayview Business Park for physical access.', type: 'claim' },
      { id: 'log-3', timestamp: '09:22', author: 'Selam Bekele', authorAvatar: 'SB', message: 'Facilities confirmed power supply to RN-YEKA-BIZ-02 mounting bracket is showing amber LED. Replacement unit being sourced.', type: 'update' },
      { id: 'log-4', timestamp: '09:28', author: 'Selam Bekele', authorAvatar: 'SB', message: 'PSU replacement in progress. ETA 20 minutes for full restoration. Tenants notified.', type: 'update' },
    ],
  },
  {
    id: 'WO-1038',
    incidentId: 'INC-4818',
    incidentTitle: 'Tarana base node beam-forming instability — sector oscillation',
    priority: 'P2',
    slaDeadline: '10:21',
    slaRemaining: '47m',
    slaBreaching: false,
    type: 'Beam-Forming Instability',
    region: 'Arada',
    node: 'BN-ARADA-NORTH-01',
    status: 'paused',
    assigneeId: 'tm-3',
    assigneeName: 'Yonas Girma',
    assigneeAvatar: 'YG',
    createdAt: '09:21',
    estimatedDuration: '90m',
    elapsedTime: '34m',
    customer: 'PacificNet Ltd. (Business Complex — 12 tenants)',
    description: 'Tarana base node BN-ARADA-NORTH-01 experiencing repeated beam-forming sector oscillation every 45 seconds. Suspected interference or misconfigured MIMO parameters on sector 3. Affects 12 business tenants sharing remote node receivers on this base node.',
    logs: [
      { id: 'log-1', timestamp: '09:21', author: 'System', authorAvatar: 'SY', message: 'Work order created from INC-4818', type: 'update' },
      { id: 'log-2', timestamp: '09:23', author: 'Yonas Girma', authorAvatar: 'YG', message: 'Claimed. Reviewing Tarana NMS beam-forming logs and sector RF statistics.', type: 'claim' },
      { id: 'log-3', timestamp: '09:31', author: 'Yonas Girma', authorAvatar: 'YG', message: 'Confirmed MIMO parameter mismatch on sector 3 (channel width 40MHz vs expected 80MHz). Preparing config change.', type: 'update' },
      { id: 'log-4', timestamp: '09:45', author: 'Yonas Girma', authorAvatar: 'YG', message: 'Paused — awaiting change window approval from Tigist Worku.', type: 'pause' },
    ],
  },
  {
    id: 'WO-1037',
    incidentId: 'INC-4817',
    incidentTitle: 'Upstream packet loss exceeds 3% — FWA backhaul link',
    priority: 'P2',
    slaDeadline: '10:19',
    slaRemaining: '45m',
    slaBreaching: false,
    type: 'Backhaul Packet Loss',
    region: 'Lideta',
    node: 'BN-LIDETA-SOUTH-03',
    status: 'assigned',
    assigneeId: null,
    assigneeName: null,
    assigneeAvatar: null,
    createdAt: '09:19',
    estimatedDuration: '30m',
    elapsedTime: '0m',
    customer: 'Axiom Networks (Single Dwelling Business)',
    description: 'Packet loss on Tarana FWA backhaul uplink from BN-LIDETA-SOUTH-03 has been at 3.2–4.8% for the past 22 minutes. Traceroute shows loss starting at second hop. Possible upstream provider issue or RF interference on the backhaul frequency band.',
    logs: [
      { id: 'log-1', timestamp: '09:19', author: 'System', authorAvatar: 'SY', message: 'Work order created from INC-4817. Awaiting assignment.', type: 'update' },
    ],
  },
  {
    id: 'WO-1035',
    incidentId: 'INC-4815',
    incidentTitle: 'Base node CPU spike — scheduling overload on sector processor',
    priority: 'P2',
    slaDeadline: '10:18',
    slaRemaining: '44m',
    slaBreaching: false,
    type: 'CPU Overload',
    region: 'Nifas Silk-Lafto',
    node: 'BN-NIFAS-EAST-01',
    status: 'assigned',
    assigneeId: 'tm-4',
    assigneeName: 'Hiwot Alemu',
    assigneeAvatar: 'HA',
    createdAt: '09:18',
    estimatedDuration: '45m',
    elapsedTime: '0m',
    customer: 'Axiom Networks (Single Dwelling Business)',
    description: 'CPU on Tarana base node BN-NIFAS-EAST-01 sustained at 94% for 18 minutes. Sector scheduling process consuming 71% CPU. Possible excessive remote node re-association requests or large number of simultaneous beam-forming recalculations across connected remote nodes.',
    logs: [
      { id: 'log-1', timestamp: '09:18', author: 'System', authorAvatar: 'SY', message: 'Work order created from INC-4815', type: 'update' },
    ],
  },
  {
    id: 'WO-1033',
    incidentId: 'INC-4813',
    incidentTitle: 'Base node memory pressure — FWA session table near capacity',
    priority: 'P3',
    slaDeadline: '12:08',
    slaRemaining: '2h 34m',
    slaBreaching: false,
    type: 'Memory Pressure',
    region: 'Addis Ketema',
    node: 'BN-ADDISKETEMA-WEST-01',
    status: 'completed',
    assigneeId: 'tm-2',
    assigneeName: 'Selam Bekele',
    assigneeAvatar: 'SB',
    createdAt: '09:08',
    estimatedDuration: '30m',
    elapsedTime: '28m',
    customer: 'BritNet Wholesale (Business Complex — 11 tenants)',
    description: 'Memory utilization on Tarana base node BN-ADDISKETEMA-WEST-01 has been at 87% for 35 minutes. FWA session table grew after 3 new remote node receivers were provisioned for BritNet Wholesale business complex tenants.',
    logs: [
      { id: 'log-1', timestamp: '09:08', author: 'System', authorAvatar: 'SY', message: 'Work order created from INC-4813', type: 'update' },
      { id: 'log-2', timestamp: '09:10', author: 'Selam Bekele', authorAvatar: 'SB', message: 'Claimed. Investigating memory allocation per Tarana process.', type: 'claim' },
      { id: 'log-3', timestamp: '09:24', author: 'Selam Bekele', authorAvatar: 'SB', message: 'Identified stale FWA session entries consuming 4GB. Cleared expired sessions — memory dropped to 71%.', type: 'update' },
      { id: 'log-4', timestamp: '09:36', author: 'Selam Bekele', authorAvatar: 'SB', message: 'Memory stable at 69% for 10 minutes. All 11 business tenants confirmed online. Resolved. Closing work order.', type: 'complete' },
    ],
  },
];

// ─── Config ───────────────────────────────────────────────────────────────────

const priorityConfig: Record<Priority, { label: string; color: string; bg: string; border: string }> = {
  P1: { label: 'Critical', color: 'text-red-400', bg: 'bg-red-400/10', border: 'border-red-400/30' },
  P2: { label: 'High', color: 'text-orange-400', bg: 'bg-orange-400/10', border: 'border-orange-400/30' },
  P3: { label: 'Medium', color: 'text-yellow-400', bg: 'bg-yellow-400/10', border: 'border-yellow-400/30' },
  P4: { label: 'Low', color: 'text-blue-400', bg: 'bg-blue-400/10', border: 'border-blue-400/30' },
};

const statusConfig: Record<ExecStatus, { label: string; color: string; bg: string; icon: React.ElementType }> = {
  assigned: { label: 'Assigned', color: 'text-blue-400', bg: 'bg-blue-400/10', icon: User },
  claimed: { label: 'Claimed', color: 'text-purple-400', bg: 'bg-purple-400/10', icon: ClipboardCheck },
  in_progress: { label: 'In Progress', color: 'text-primary', bg: 'bg-primary/10', icon: Activity },
  paused: { label: 'Paused', color: 'text-yellow-400', bg: 'bg-yellow-400/10', icon: Pause },
  completed: { label: 'Completed', color: 'text-success', bg: 'bg-success/10', icon: CheckCircle2 },
};

const logTypeIcon: Record<UpdateLog['type'], { icon: React.ElementType; color: string }> = {
  claim: { icon: ClipboardCheck, color: 'text-purple-400' },
  start: { icon: Play, color: 'text-primary' },
  pause: { icon: Pause, color: 'text-yellow-400' },
  resume: { icon: RotateCcw, color: 'text-blue-400' },
  update: { icon: MessageSquarePlus, color: 'text-muted' },
  complete: { icon: CheckCircle2, color: 'text-success' },
};

// ─── Sub-components ───────────────────────────────────────────────────────────

function PriorityBadge({ priority }: { priority: Priority }) {
  const cfg = priorityConfig[priority];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-bold border ${cfg.color} ${cfg.bg} ${cfg.border}`}>
      {priority}
    </span>
  );
}

function StatusBadge({ status }: { status: ExecStatus }) {
  const cfg = statusConfig[status];
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${cfg.color} ${cfg.bg}`}>
      <Icon size={11} />
      {cfg.label}
    </span>
  );
}

function Avatar({ initials, size = 'sm' }: { initials: string; size?: 'sm' | 'md' }) {
  const colors: Record<string, string> = {
    DT: 'bg-blue-500/20 text-blue-400',
    SB: 'bg-green-500/20 text-green-400',
    YG: 'bg-purple-500/20 text-purple-400',
    HA: 'bg-pink-500/20 text-pink-400',
    BH: 'bg-orange-500/20 text-orange-400',
    TW: 'bg-cyan-500/20 text-cyan-400',
    AM: 'bg-yellow-500/20 text-yellow-400',
    SY: 'bg-surface-raised text-muted',
  };
  const sizeClass = size === 'md' ? 'w-8 h-8 text-sm' : 'w-6 h-6 text-[10px]';
  return (
    <div className={`${sizeClass} rounded-full flex items-center justify-center font-bold flex-shrink-0 ${colors[initials] || 'bg-primary/20 text-primary'}`}>
      {initials}
    </div>
  );
}

// Email delivery status pill
function EmailStatusPill({ result }: { result: EmailDeliveryResult }) {
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full
      ${result.status === 'sent' ? 'bg-success/10 text-success' :
        result.status === 'failed'? 'bg-danger/10 text-danger' : 'bg-primary/10 text-primary'}`}>
      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
        result.status === 'sent' ? 'bg-success' :
        result.status === 'failed'? 'bg-danger' : 'bg-primary animate-pulse'}`} />
      {result.status === 'sent' ? 'Notified' : result.status === 'failed' ? 'Notify failed' : 'Notifying…'}
    </span>
  );
}

// ─── Log Entry Modal ──────────────────────────────────────────────────────────

interface LogModalProps {
  workOrderId: string;
  onClose: () => void;
  onSubmit: (woId: string, message: string) => void;
}

function LogUpdateModal({ workOrderId, onClose, onSubmit }: LogModalProps) {
  const [message, setMessage] = useState('');
  const handleSubmit = () => {
    if (message.trim()) {
      onSubmit(workOrderId, message.trim());
      onClose();
    }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-surface border border-border rounded-xl w-full max-w-md mx-4 shadow-2xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div className="flex items-center gap-2">
            <MessageSquarePlus size={16} className="text-primary" />
            <h3 className="text-sm font-semibold text-foreground">Log Update — {workOrderId}</h3>
          </div>
          <button onClick={onClose} className="text-muted hover:text-foreground transition-colors">
            <X size={16} />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-medium text-muted mb-2">Update Message</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Describe what you've done, findings, or next steps..."
              rows={4}
              className="w-full bg-surface-raised border border-border rounded-lg px-3 py-2.5 text-sm text-foreground placeholder:text-muted focus:outline-none focus:border-primary/50 resize-none"
            />
          </div>
          <div className="flex items-center gap-2 text-xs text-muted">
            <User size={12} />
            <span>Logging as <span className="text-foreground font-medium">{currentUser.name}</span></span>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-border">
          <button onClick={onClose} className="px-4 py-2 text-sm text-muted hover:text-foreground transition-colors">
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!message.trim()}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white text-sm font-medium rounded-lg hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <Send size={13} />
            Submit Update
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Work Order Card ──────────────────────────────────────────────────────────

interface WOCardProps {
  wo: WorkOrderExec;
  isExpanded: boolean;
  onToggleExpand: (id: string) => void;
  onClaim: (id: string) => void;
  onStart: (id: string) => void;
  onPause: (id: string) => void;
  onResume: (id: string) => void;
  onComplete: (id: string) => void;
  onLogUpdate: (id: string) => void;
  emailStatus?: EmailDeliveryResult;
}

function WorkOrderCard({ wo, isExpanded, onToggleExpand, onClaim, onStart, onPause, onResume, onComplete, onLogUpdate, emailStatus }: WOCardProps) {
  const pCfg = priorityConfig[wo.priority];
  const isMyOrder = wo.assigneeId === currentUser.id;
  const canClaim = wo.status === 'assigned' && !wo.assigneeId;
  const canStart = isMyOrder && wo.status === 'claimed';
  const canPause = isMyOrder && wo.status === 'in_progress';
  const canResume = isMyOrder && wo.status === 'paused';
  const canComplete = isMyOrder && (wo.status === 'in_progress' || wo.status === 'paused');
  const canLog = isMyOrder && wo.status !== 'completed' && wo.status !== 'assigned';

  return (
    <div className={`bg-surface border rounded-xl overflow-hidden transition-all duration-200 ${wo.slaBreaching ? 'border-red-400/40' : 'border-border'} ${wo.status === 'completed' ? 'opacity-70' : ''}`}>
      {/* Card Header */}
      <div className="px-4 py-3.5">
        <div className="flex items-start gap-3">
          {/* Priority stripe */}
          <div className={`w-1 h-full min-h-[40px] rounded-full flex-shrink-0 ${wo.priority === 'P1' ? 'bg-red-400' : wo.priority === 'P2' ? 'bg-orange-400' : wo.priority === 'P3' ? 'bg-yellow-400' : 'bg-blue-400'}`} />

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1.5">
              <span className="text-xs font-mono font-bold text-foreground">{wo.id}</span>
              <span className="text-xs text-muted font-mono">↔ {wo.incidentId}</span>
              <PriorityBadge priority={wo.priority} />
              <StatusBadge status={wo.status} />
              {wo.slaBreaching && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-bold bg-red-400/10 text-red-400 border border-red-400/30 animate-pulse">
                  <AlertTriangle size={10} /> SLA BREACH
                </span>
              )}
              {emailStatus && <EmailStatusPill result={emailStatus} />}
            </div>
            <p className="text-sm font-medium text-foreground truncate">{wo.incidentTitle}</p>
            <div className="flex items-center gap-4 mt-2 flex-wrap">
              <span className="flex items-center gap-1 text-xs text-muted"><Tag size={11} />{wo.type}</span>
              <span className="flex items-center gap-1 text-xs text-muted"><MapPin size={11} />{wo.region}</span>
              <span className="flex items-center gap-1 text-xs text-muted"><Server size={11} />{wo.node}</span>
              <span className="flex items-center gap-1 text-xs text-muted"><Timer size={11} />Est. {wo.estimatedDuration}</span>
              {wo.elapsedTime !== '0m' && (
                <span className="flex items-center gap-1 text-xs text-primary"><Activity size={11} />Elapsed: {wo.elapsedTime}</span>
              )}
              <span className={`flex items-center gap-1 text-xs font-medium ${wo.slaBreaching ? 'text-red-400' : 'text-muted'}`}>
                <CalendarClock size={11} />SLA: {wo.slaDeadline} ({wo.slaRemaining})
              </span>
            </div>
          </div>

          {/* Assignee + Actions */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {wo.assigneeAvatar ? (
              <div className="flex items-center gap-1.5">
                <Avatar initials={wo.assigneeAvatar} />
                <span className="text-xs text-muted hidden lg:block">{wo.assigneeName}</span>
              </div>
            ) : (
              <span className="text-xs text-muted italic">Unassigned</span>
            )}

            {/* Action buttons */}
            <div className="flex items-center gap-1">
              {canClaim && (
                <button onClick={() => onClaim(wo.id)} className="flex items-center gap-1 px-2.5 py-1.5 bg-purple-500/15 text-purple-400 border border-purple-400/30 rounded-lg text-xs font-semibold hover:bg-purple-500/25 transition-colors">
                  <ClipboardCheck size={12} /> Claim
                </button>
              )}
              {canStart && (
                <button onClick={() => onStart(wo.id)} className="flex items-center gap-1 px-2.5 py-1.5 bg-primary/15 text-primary border border-primary/30 rounded-lg text-xs font-semibold hover:bg-primary/25 transition-colors">
                  <Play size={12} /> Start
                </button>
              )}
              {canPause && (
                <button onClick={() => onPause(wo.id)} className="flex items-center gap-1 px-2.5 py-1.5 bg-yellow-400/10 text-yellow-400 border border-yellow-400/30 rounded-lg text-xs font-semibold hover:bg-yellow-400/20 transition-colors">
                  <Pause size={12} /> Pause
                </button>
              )}
              {canResume && (
                <button onClick={() => onResume(wo.id)} className="flex items-center gap-1 px-2.5 py-1.5 bg-blue-400/10 text-blue-400 border border-blue-400/30 rounded-lg text-xs font-semibold hover:bg-blue-400/20 transition-colors">
                  <RotateCcw size={12} /> Resume
                </button>
              )}
              {canComplete && (
                <button onClick={() => onComplete(wo.id)} className="flex items-center gap-1 px-2.5 py-1.5 bg-success/10 text-success border border-success/30 rounded-lg text-xs font-semibold hover:bg-success/20 transition-colors">
                  <CheckCircle2 size={12} /> Complete
                </button>
              )}
              {canLog && (
                <button onClick={() => onLogUpdate(wo.id)} className="flex items-center gap-1 px-2.5 py-1.5 bg-surface-raised text-muted border border-border rounded-lg text-xs font-semibold hover:text-foreground hover:border-primary/40 transition-colors">
                  <MessageSquarePlus size={12} /> Log
                </button>
              )}
            </div>

            <button
              onClick={() => onToggleExpand(wo.id)}
              className="p-1.5 text-muted hover:text-foreground rounded-lg hover:bg-surface-raised transition-colors"
            >
              {isExpanded ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
            </button>
          </div>
        </div>
      </div>

      {/* Expanded Detail */}
      {isExpanded && (
        <div className="border-t border-border bg-surface-raised/40">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-0 divide-y lg:divide-y-0 lg:divide-x divide-border">
            {/* Description */}
            <div className="p-4">
              <h4 className="text-xs font-semibold text-muted uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <Link2 size={11} /> Incident Details
              </h4>
              <p className="text-sm text-foreground-muted leading-relaxed">{wo.description}</p>
              <div className="mt-3 grid grid-cols-2 gap-2">
                <div className="bg-surface border border-border rounded-lg p-2.5">
                  <p className="text-[10px] text-muted uppercase tracking-wider mb-0.5">Customer</p>
                  <p className="text-xs font-semibold text-foreground">{wo.customer}</p>
                </div>
                <div className="bg-surface border border-border rounded-lg p-2.5">
                  <p className="text-[10px] text-muted uppercase tracking-wider mb-0.5">Created</p>
                  <p className="text-xs font-semibold text-foreground">{wo.createdAt}</p>
                </div>
              </div>
            </div>

            {/* Activity Log */}
            <div className="p-4">
              <h4 className="text-xs font-semibold text-muted uppercase tracking-wider mb-3 flex items-center gap-1.5">
                <Activity size={11} /> Activity Log
              </h4>
              <div className="space-y-2.5 max-h-48 overflow-y-auto pr-1">
                {wo.logs.map((log) => {
                  const logCfg = logTypeIcon[log.type];
                  const LogIcon = logCfg.icon;
                  return (
                    <div key={log.id} className="flex items-start gap-2.5">
                      <div className={`w-5 h-5 rounded-full bg-surface border border-border flex items-center justify-center flex-shrink-0 mt-0.5 ${logCfg.color}`}>
                        <LogIcon size={10} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="text-xs font-semibold text-foreground">{log.author}</span>
                          <span className="text-[10px] text-muted font-mono">{log.timestamp}</span>
                        </div>
                        <p className="text-xs text-foreground-muted leading-relaxed">{log.message}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Team Collaboration — Notes & Comments */}
            <div className="min-h-[280px] flex flex-col">
              <WorkOrderComments workOrderId={wo.id} currentUser={currentUser} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function WorkOrderExecutionPage() {
  const [workOrders, setWorkOrders] = useState<WorkOrderExec[]>(initialWorkOrders);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set(['WO-1041']));
  const [logModalWoId, setLogModalWoId] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<ExecStatus | 'all'>('all');
  const [filterPriority, setFilterPriority] = useState<Priority | 'all'>('all');
  const [search, setSearch] = useState('');
  const [emailStatuses, setEmailStatuses] = useState<Record<string, EmailDeliveryResult>>({});
  const { toasts, addToast, dismissToast } = useToast();

  const now = new Date();
  const timeStr = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });

  // Broadcast sidebar badge for active/breaching execution orders
  const broadcastBadge = useCallback((orders: WorkOrderExec[]) => {
    const active = orders.filter((w) => w.status !== 'completed' && (w.slaBreaching || w.status === 'claimed' || w.status === 'in_progress')).length;
    localStorage.setItem('sidebar_badge_execution', String(active));
    window.dispatchEvent(new CustomEvent('sidebar-badge-update', { detail: { key: 'sidebar_badge_execution', value: active } }));
  }, []);

  useEffect(() => {
    broadcastBadge(workOrders);
  }, [workOrders, broadcastBadge]);

  // Listen for email delivery events
  useEffect(() => {
    const unsubscribe = onEmailDelivery((result) => {
      setEmailStatuses((prev) => ({ ...prev, [result.workOrderId]: result }));
      if (result.status === 'sent') {
        addToast(`Notification sent for ${result.workOrderId}`, 'info', 2500);
      } else if (result.status === 'failed') {
        addToast(`Email delivery failed for ${result.workOrderId}`, 'error', 4000);
      }
    });
    return unsubscribe;
  }, [addToast]);

  const addLog = (woId: string, message: string, type: UpdateLog['type'] = 'update') => {
    setWorkOrders((prev) =>
      prev.map((wo) =>
        wo.id === woId
          ? {
              ...wo,
              logs: [
                ...wo.logs,
                {
                  id: `log-${Date.now()}`,
                  timestamp: timeStr,
                  author: currentUser.name,
                  authorAvatar: currentUser.avatar,
                  message,
                  type,
                },
              ],
            }
          : wo
      )
    );
  };

  const handleClaim = (id: string) => {
    const wo = workOrders.find((w) => w.id === id);
    setWorkOrders((prev) =>
      prev.map((wo) =>
        wo.id === id
          ? { ...wo, status: 'claimed', assigneeId: currentUser.id, assigneeName: currentUser.name, assigneeAvatar: currentUser.avatar }
          : wo
      )
    );
    addLog(id, `Claimed by ${currentUser.name}. Starting investigation.`, 'claim');
    setExpandedIds((prev) => new Set([...prev, id]));
    addToast(`${id} claimed successfully`, 'success');
    if (wo) {
      sendWorkOrderStatusChangeNotification({
        workOrderId: wo.id,
        workOrderTitle: wo.incidentTitle,
        oldStatus: wo.status,
        newStatus: 'claimed',
        updatedBy: currentUser.name,
        node: wo.node,
        woRegion: wo.region,
        priority: wo.priority,
        slaDeadline: wo.slaDeadline,
        recipients: ['team@netopsai.com'],
      });
    }
  };

  const handleStart = (id: string) => {
    const wo = workOrders.find((w) => w.id === id);
    setWorkOrders((prev) => prev.map((wo) => (wo.id === id ? { ...wo, status: 'in_progress' } : wo)));
    addLog(id, 'Work started. Actively working on resolution.', 'start');
    addToast(`${id} started — work in progress`, 'success');
    if (wo) {
      sendWorkOrderStatusChangeNotification({
        workOrderId: wo.id,
        workOrderTitle: wo.incidentTitle,
        oldStatus: wo.status,
        newStatus: 'in_progress',
        updatedBy: currentUser.name,
        node: wo.node,
        woRegion: wo.region,
        priority: wo.priority,
        slaDeadline: wo.slaDeadline,
        recipients: ['team@netopsai.com'],
      });
      if (wo.slaBreaching) {
        sendSlaApproachingNotification({
          workOrderId: wo.id,
          workOrderTitle: wo.incidentTitle,
          incidentId: wo.incidentId,
          priority: wo.priority,
          slaDeadline: wo.slaDeadline,
          slaRemaining: wo.slaRemaining,
          assigneeName: currentUser.name,
          node: wo.node,
          woRegion: wo.region,
          woCustomer: wo.customer,
          woStatus: 'in_progress',
          recipients: ['team@netopsai.com'],
        });
      }
    }
  };

  const handlePause = (id: string) => {
    const wo = workOrders.find((w) => w.id === id);
    setWorkOrders((prev) => prev.map((wo) => (wo.id === id ? { ...wo, status: 'paused' } : wo)));
    addLog(id, 'Work paused.', 'pause');
    addToast(`${id} paused`, 'warning');
    if (wo) {
      sendWorkOrderStatusChangeNotification({
        workOrderId: wo.id,
        workOrderTitle: wo.incidentTitle,
        oldStatus: wo.status,
        newStatus: 'paused',
        updatedBy: currentUser.name,
        node: wo.node,
        woRegion: wo.region,
        priority: wo.priority,
        slaDeadline: wo.slaDeadline,
        recipients: ['team@netopsai.com'],
      });
    }
  };

  const handleResume = (id: string) => {
    const wo = workOrders.find((w) => w.id === id);
    setWorkOrders((prev) => prev.map((wo) => (wo.id === id ? { ...wo, status: 'in_progress' } : wo)));
    addLog(id, 'Work resumed.', 'resume');
    addToast(`${id} resumed`, 'success');
    if (wo) {
      sendWorkOrderStatusChangeNotification({
        workOrderId: wo.id,
        workOrderTitle: wo.incidentTitle,
        oldStatus: wo.status,
        newStatus: 'in_progress',
        updatedBy: currentUser.name,
        node: wo.node,
        woRegion: wo.region,
        priority: wo.priority,
        slaDeadline: wo.slaDeadline,
        recipients: ['team@netopsai.com'],
      });
    }
  };

  const handleComplete = (id: string) => {
    const wo = workOrders.find((w) => w.id === id);
    setWorkOrders((prev) => prev.map((wo) => (wo.id === id ? { ...wo, status: 'completed' } : wo)));
    addLog(id, `Work order resolved and closed by ${currentUser.name}.`, 'complete');
    addToast(`${id} completed and closed`, 'success');
    if (wo) {
      sendWorkOrderStatusChangeNotification({
        workOrderId: wo.id,
        workOrderTitle: wo.incidentTitle,
        oldStatus: wo.status,
        newStatus: 'completed',
        updatedBy: currentUser.name,
        node: wo.node,
        woRegion: wo.region,
        priority: wo.priority,
        slaDeadline: wo.slaDeadline,
        recipients: ['team@netopsai.com'],
      });
    }
  };

  const handleLogSubmit = (woId: string, message: string) => {
    addLog(woId, message, 'update');
    addToast(`Update logged for ${woId}`, 'info');
  };

  const toggleExpand = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const filtered = useMemo(() => {
    return workOrders.filter((wo) => {
      if (filterStatus !== 'all' && wo.status !== filterStatus) return false;
      if (filterPriority !== 'all' && wo.priority !== filterPriority) return false;
      if (search) {
        const q = search.toLowerCase();
        if (!wo.id.toLowerCase().includes(q) && !wo.incidentTitle.toLowerCase().includes(q) && !wo.node.toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [workOrders, filterStatus, filterPriority, search]);

  // Stats
  const stats = useMemo(() => ({
    total: workOrders.length,
    myActive: workOrders.filter((w) => w.assigneeId === currentUser.id && w.status !== 'completed').length,
    inProgress: workOrders.filter((w) => w.status === 'in_progress').length,
    breaching: workOrders.filter((w) => w.slaBreaching && w.status !== 'completed').length,
    completed: workOrders.filter((w) => w.status === 'completed').length,
  }), [workOrders]);

  return (
    <AppLayout>
      <div className="flex flex-col h-full min-h-0 bg-background">
        {/* Header */}
        <div className="flex-shrink-0 px-6 py-4 border-b border-border bg-surface">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center">
                <ClipboardCheck size={18} className="text-primary" />
              </div>
              <div>
                <h1 className="text-base font-semibold text-foreground">Work Order Execution</h1>
                <p className="text-xs text-muted">Claim, execute, and track your assigned work orders</p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-xs text-muted">
              <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-primary text-[10px] font-bold">
                {currentUser.avatar}
              </div>
              <span className="font-medium text-foreground">{currentUser.name}</span>
              <span className="text-muted">·</span>
              <span>{currentUser.role}</span>
            </div>
          </div>
        </div>

        {/* KPI Strip */}
        <div className="flex-shrink-0 px-6 py-3 border-b border-border bg-surface/50">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2 px-3 py-1.5 bg-surface-raised rounded-lg border border-border">
              <Zap size={13} className="text-primary" />
              <span className="text-xs text-muted">My Active</span>
              <span className="text-sm font-bold text-foreground">{stats.myActive}</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 bg-surface-raised rounded-lg border border-border">
              <Activity size={13} className="text-primary" />
              <span className="text-xs text-muted">In Progress</span>
              <span className="text-sm font-bold text-foreground">{stats.inProgress}</span>
            </div>
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border ${stats.breaching > 0 ? 'bg-red-400/10 border-red-400/30' : 'bg-surface-raised border-border'}`}>
              <AlertTriangle size={13} className={stats.breaching > 0 ? 'text-red-400' : 'text-muted'} />
              <span className="text-xs text-muted">SLA Breaching</span>
              <span className={`text-sm font-bold ${stats.breaching > 0 ? 'text-red-400' : 'text-foreground'}`}>{stats.breaching}</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 bg-surface-raised rounded-lg border border-border">
              <CheckCircle2 size={13} className="text-success" />
              <span className="text-xs text-muted">Completed</span>
              <span className="text-sm font-bold text-success">{stats.completed}</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 bg-surface-raised rounded-lg border border-border">
              <Clock size={13} className="text-muted" />
              <span className="text-xs text-muted">Total</span>
              <span className="text-sm font-bold text-foreground">{stats.total}</span>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex-shrink-0 px-6 py-3 border-b border-border bg-surface/30">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[200px] max-w-xs">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
              <input
                type="text"
                placeholder="Search WO ID, title, node..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-8 pr-3 py-2 bg-surface-raised border border-border rounded-lg text-sm text-foreground placeholder:text-muted focus:outline-none focus:border-primary/50"
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter size={13} className="text-muted" />
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value as ExecStatus | 'all')}
                className="bg-surface-raised border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:border-primary/50"
              >
                <option value="all">All Statuses</option>
                <option value="assigned">Assigned</option>
                <option value="claimed">Claimed</option>
                <option value="in_progress">In Progress</option>
                <option value="paused">Paused</option>
                <option value="completed">Completed</option>
              </select>
              <select
                value={filterPriority}
                onChange={(e) => setFilterPriority(e.target.value as Priority | 'all')}
                className="bg-surface-raised border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:border-primary/50"
              >
                <option value="all">All Priorities</option>
                <option value="P1">P1 — Critical</option>
                <option value="P2">P2 — High</option>
                <option value="P3">P3 — Medium</option>
                <option value="P4">P4 — Low</option>
              </select>
            </div>
            <span className="text-xs text-muted ml-auto">{filtered.length} work order{filtered.length !== 1 ? 's' : ''}</span>
          </div>
        </div>

        {/* Work Order List */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
          {filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <ClipboardCheck size={36} className="text-muted mb-3 opacity-40" />
              <p className="text-sm font-medium text-muted">No work orders match your filters</p>
              <p className="text-xs text-muted mt-1 opacity-70">Try adjusting the status or priority filter</p>
            </div>
          ) : (
            filtered.map((wo) => (
              <WorkOrderCard
                key={wo.id}
                wo={wo}
                isExpanded={expandedIds.has(wo.id)}
                onToggleExpand={toggleExpand}
                onClaim={handleClaim}
                onStart={handleStart}
                onPause={handlePause}
                onResume={handleResume}
                onComplete={handleComplete}
                onLogUpdate={(id) => setLogModalWoId(id)}
                emailStatus={emailStatuses[wo.id]}
              />
            ))
          )}
        </div>
      </div>

      {/* Log Update Modal */}
      {logModalWoId && (
        <LogUpdateModal
          workOrderId={logModalWoId}
          onClose={() => setLogModalWoId(null)}
          onSubmit={handleLogSubmit}
        />
      )}

      {/* Toast Notifications */}
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
    </AppLayout>
  );
}
